import {
  Button,
  DisabledContext,
  Flex,
  Grid,
  Heading,
  Input,
  Loader,
  Select,
  Spacer,
  Spinner,
  Text,
  Toggle,
  UnitInput,
} from '@oliasoft-open-source/react-ui-library';
import translations from 'client/internationalization/translation-map.json';
import {
  associatedDataSet, upsertAssociatedData, getObjectsByTypeAndParentId,
} from 'client/store/entities/object/object';
import {
  getAssociatedObject, getObjectsByTypeAndParent, getObjectsUpdateState,
} from 'client/store/entities/object/selectors';
import { RootState } from 'client/store/configureStore';
import { EntityType } from 'common/enums/entity-enums';
import { KnowEdmTableData } from 'common/services/configs/trajectories/table-enums';
import {
  JSXElementConstructor, ReactElement,
  useState,
  Fragment,
  useMemo,
  useEffect,
  ChangeEvent,
  useContext,
  Dispatch,
  SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ability, ActionMap } from '@oliasoft/authorization-ui';
import { caslEnums } from 'common/enums/casl-enums';
import { entityAssociatedDataMap } from 'common/associated-data-viewer/utils';
import { covarianceMatrixOptions } from './associated-data-viewer.utils';
import { TieOnTypes } from 'common/services/configs/trajectories/enums';
import { getObjectName } from 'client/views/explore/hierarchy/object-parts';
import { HierarchyLevelType, IObject } from 'common/interfaces/hierarchy-entities';
import { getIPMFilesSelector } from 'client/store/entities/ipm-files/selectors';
import { unum } from '@oliasoft-open-source/units';
import { getPreferredUnit } from 'common/table-viewer/utils';
import { UnitContext } from 'client/views/hoc/with-unit-template';

type TAssociatedDataProps = {
  objectid: string;
  nodeid: string;
  designid: string;
  archiveid: string;
  entityType: EntityType;
  setSelectedData?: Dispatch<SetStateAction<IObject | null>>;
  viewOnly?: boolean;
};

export const AssociatedData = ({
  objectid, nodeid, designid, archiveid, entityType, setSelectedData, viewOnly = false
}: TAssociatedDataProps) => {
  const unitTemplate = useContext(UnitContext);

  const { t } = useTranslation();
  const [disabled, setDisabledState] = useState(true);
  const isLoadingUpdate = useSelector(getObjectsUpdateState);
  const associatedData = useSelector(state => getAssociatedObject(state, objectid));
  const ipmFiles = useSelector(getIPMFilesSelector);

  const surveyRuns = useSelector(state => getObjectsByTypeAndParent(state, EntityType.SurveyRun, HierarchyLevelType.Design, designid));
  const geomagneticParameters = useSelector(state => getObjectsByTypeAndParent(state, EntityType.GeomagneticParameters, HierarchyLevelType.Design, designid));
  const holeSizes = useSelector(state => getObjectsByTypeAndParent(state, EntityType.HoleSize, HierarchyLevelType.Design, designid));
  const covarianceMatrices = useSelector(state => getObjectsByTypeAndParent(state, EntityType.CovarianceMatrix, HierarchyLevelType.Design, designid));
  const hierarchyNodes = useSelector((state: RootState) => state.entities.nodeTree.items);
  const covarianceMatrixSelectOptions = useMemo(
    () => covarianceMatrixOptions(covarianceMatrices, hierarchyNodes),
    [covarianceMatrices, hierarchyNodes],
  );
  const selectOptionsByKey: Partial<Record<KnowEdmTableData, typeof geomagneticParameters>> = {
    [KnowEdmTableData.GeomagneticParametersDefinition]: geomagneticParameters,
    [KnowEdmTableData.HoleSize]: holeSizes,
    [KnowEdmTableData.CovarianceMatrix]: covarianceMatrices,
  };
  const tieOnRunIsActive = associatedData?.[KnowEdmTableData.tieType] === TieOnTypes.Run;
  const associatedDataMap = entityAssociatedDataMap.get(entityType);

  const handleToggle = (name: string) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    handleUpdateAssociatedData({ target: { name, value } } as any);
  };

  const surveyRunOptions = useMemo(() => {
    if (!surveyRuns?.length) return [];
    return surveyRuns
      .filter(object => object.objectid !== objectid)
      .map(object => ({ label: getObjectName(object, object.associateddata), value: object.associateddata?.surveyrunsassociateddataid }));
  }, [surveyRuns, objectid]);

  const dispatch = useDispatch();

  const handleUpdateAssociatedData = (event: React.ChangeEvent<{ value: string, name: string }>) => {
    const { name, value } = event.target;
    dispatch(associatedDataSet({
      objectid,
      associatedData: {
        ...associatedData, [name]: value,
      },
    }));
  };

  const handleRevertEdit = () => {
    // a bit hackish to revert state
    setDisabledState(true);
  };

  const updatedAssociatedData = () => {
    setDisabledState(true);
    dispatch(upsertAssociatedData(objectid, nodeid, associatedData, entityType, designid));
  };

  const getInputNode = (map: Map<any, any>, key: string) => {
    const { unit, quantity, locked } = map.get(key);
    let { options } = map.get(key);
    const value = associatedData?.[key];

    if (key === KnowEdmTableData.IPMFile) {
      options = ipmFiles?.map((object) => ({ label: object.name, value: object.objectid }));
    }

    const selectObjects = selectOptionsByKey[key as KnowEdmTableData];
    if (selectObjects) {
      options = selectObjects.map((object) => ({ label: object.name, value: object.objectid }));
    }

    if (key === KnowEdmTableData.CovarianceMatrix) {
      options = covarianceMatrixSelectOptions;
    }

    if (tieOnRunIsActive && key === KnowEdmTableData.tieOnRun) {
      options = surveyRunOptions;
    }

    if (tieOnRunIsActive && key === KnowEdmTableData.IncOnly) {
      options = [true, false].map((i) => ({ label: String(i), value: i }));
    }

    if (key === KnowEdmTableData.IncOnly) {
      return (
        <Toggle
          checked={!!value}
          disabled={disabled}
          onChange={handleToggle(key)}
        />
      );
    }

    if (options?.length) {
      const hasRefData = selectObjects?.length;
      return (
        <Flex alignItems="center" gap="var(--padding-xs)">
          <Select
            onChange={handleUpdateAssociatedData}
            name={key}
            value={value}
            options={options}
            placeholder={`Select ${map?.get(key)?.label}`}
            width={hasRefData ? "160px" : "200px"}
            autoLayerWidth
          />
          {!viewOnly && hasRefData &&
            <Button round basic colored="muted" icon="link" ignoreDisabledContext onClick={() => {
              const found = selectObjects.find(i => i.objectid === value)
              if (found) {
                setSelectedData?.(found)
              }
            }} />}
        </Flex>
      );
    }
    if (unit) {
      const unitTemplateUnit = getPreferredUnit(quantity, unitTemplate)
      const convertedValue = String(unum(value, unitTemplateUnit!, unit)) + '|' + unitTemplateUnit
      return <UnitInput
        unitTemplate={unitTemplate}
        initUnit={unitTemplateUnit}
        unitkey={quantity}
        name={key}
        value={convertedValue}
        // @ts-ignore
        onChange={handleUpdateAssociatedData}
        width="200px"
        disabled={locked}
      />
    }
    return <Input
      name={key}
      value={value}
      onChange={handleUpdateAssociatedData}
      width="200px"
      disabled={locked}
    />
  };

  useEffect(() => {
    dispatch(getObjectsByTypeAndParentId(EntityType.IPMFile, HierarchyLevelType.Archive, archiveid));
  }, [archiveid]);

  const nodes: ReactElement<any, string | JSXElementConstructor<any>>[] = [];
  associatedDataMap?.forEach((_value, key, map) => {
    if (key === KnowEdmTableData.tieOnRun && !tieOnRunIsActive) return;
    const inputNode = getInputNode(map, key);
    const node = (
      <Fragment key={key as string}>
        <Text bold>{map?.get(key)?.label}</Text>
        <Text>{inputNode}</Text>
      </Fragment>
    );
    nodes.push(node);
  });

  if (isLoadingUpdate) {
    return (
      <Loader height="200px" theme="light">
        <Spinner dark />
      </Loader>
    );
  }
  return (
    <div>
      <Heading>Associated data</Heading>
      <Spacer />
      <Grid columns="35% 35%" gap="var(--padding-xs)">
        <DisabledContext.Provider value={disabled}>
          {nodes}
        </DisabledContext.Provider>

        {!viewOnly && (
          <>
            <Button
              disabled={disabled}
              label={t(translations.save)}
              onClick={updatedAssociatedData}
            />

            {disabled ? (
              <Button
                disabled={ability.cannot(
                  ActionMap.MANAGE,
                  caslEnums.structuredDataEntities
                )}
                label={t(translations.edit)}
                onClick={() => setDisabledState(false)}
              />
            ) : (
              <Button
                label={t(translations.cancel)}
                onClick={handleRevertEdit}
              />
            )}
          </>
        )}
      </Grid>
    </div>
  );
};
